import { createContext, useContext, useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getUser, updateUser } from "../api/user.api";
import { UpdateUserRow, UserRow } from "../api/user.type";
import { useAuthContext } from "../containers/AuthContextWrapper";
import { useToastContext } from "../containers/ToastContextWrapper";

interface UpdateUserInput {
  user: UpdateUserRow;
  contactId?: string;
  relatedUserRole?: string;
}

interface UserContextType {
  user?: UserRow | null;
  isLoading: boolean;
  isUpdating: boolean;
  update: (input: UpdateUserInput) => Promise<UserRow>;
  isAdmin: boolean;
}

export const UserContext = createContext<UserContextType>({
  user: null,
  isLoading: true,
  isUpdating: false,
  update: async () => ({ id: "" } as UserRow),
  isAdmin: false,
});

export const UserContextWrapper = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { showToast } = useToastContext();

  const queryClient = useQueryClient();

  const { session } = useAuthContext();

  const [isAdmin, setIsAdmin] = useState(false);

  const { data: user, isLoading } = useQuery({
    queryFn: getUser,
    queryKey: ["user", session?.user?.id],
    enabled: !!session?.user?.id,
    refetchOnWindowFocus: true,
    refetchIntervalInBackground: true,
  });

  // Handle user data changes with useEffect
  useEffect(() => {
    if (user) {
      if (user.type && user.type === "admin") {
        setIsAdmin(true);
      } else {
        setIsAdmin(false);
      }
    }
  }, [user]);

  const { mutateAsync: update, isPending: isUpdating } = useMutation<
    UserRow,
    Error,
    UpdateUserInput
  >({
    mutationFn: ({ user, contactId, relatedUserRole }) =>
      updateUser(user, contactId, relatedUserRole),
    onSettled() {
      queryClient.invalidateQueries({ queryKey: ["user"] });
    },
    onError(error) {
      console.log(`Error updating user: ${error}`);
    },
    onSuccess() {
      showToast({
        title: "Success",
        description: "Account details updated",
      });
    },
  });

  return (
    <UserContext.Provider
      value={{ user, isLoading, isUpdating, update, isAdmin }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUserContext = () => useContext(UserContext);
